/**
 * Organic Chemistry Primer drill bank.
 *
 * Topics covered (intro level, suitable for HS / pre-AP bridging into AP Chem
 * or biology):
 *   - Functional group identification
 *   - Alkane / alkene / alkyne naming (C1–C10 prefixes)
 *   - Isomers vs same compound
 *   - Reading skeletal (line-angle) structures
 */

export type OrgCategory =
  | "functional-group"
  | "naming"
  | "isomers"
  | "skeletal"
  | "general";

export type OrgDifficulty = "easy" | "medium" | "hard";

export const ORG_CATEGORY_LABELS: Record<OrgCategory, string> = {
  "functional-group": "Functional Groups",
  naming: "IUPAC Naming",
  isomers: "Isomers",
  skeletal: "Skeletal Structures",
  general: "General Concepts",
};

export interface FunctionalGroup {
  name: string;
  /** General formula / line representation (R = rest of molecule). */
  formula: string;
  /** Suffix or prefix that signals this group in IUPAC names. */
  nameMarker: string;
  /** Everyday example. */
  example: string;
  /** Why students should care. */
  whyItMatters: string;
}

/** The 10 functional groups every HS / first-year student must recognise. */
export const FUNCTIONAL_GROUPS: FunctionalGroup[] = [
  {
    name: "Alkane",
    formula: "C–C single bonds only",
    nameMarker: "-ane",
    example: "Methane (CH₄), propane (C₃H₈)",
    whyItMatters: "Saturated hydrocarbons. Fuels, lubricants. Unreactive baseline.",
  },
  {
    name: "Alkene",
    formula: "C=C double bond",
    nameMarker: "-ene",
    example: "Ethene (C₂H₄), the gas that ripens fruit",
    whyItMatters: "Unsaturated. Building block for plastics (polyethylene).",
  },
  {
    name: "Alkyne",
    formula: "C≡C triple bond",
    nameMarker: "-yne",
    example: "Ethyne / acetylene (C₂H₂), welding torches",
    whyItMatters: "Highly reactive; very high-energy bond.",
  },
  {
    name: "Alcohol",
    formula: "R–OH",
    nameMarker: "-ol",
    example: "Ethanol (C₂H₅OH)",
    whyItMatters: "Polar. Hydrogen bonds → high boiling points; soluble in water.",
  },
  {
    name: "Carboxylic acid",
    formula: "R–COOH",
    nameMarker: "-oic acid",
    example: "Acetic acid (CH₃COOH) — vinegar",
    whyItMatters: "Weak acids. Make up amino acids and fatty acids.",
  },
  {
    name: "Aldehyde",
    formula: "R–CHO",
    nameMarker: "-al",
    example: "Methanal / formaldehyde (HCHO)",
    whyItMatters: "Carbonyl on a terminal carbon. Easily oxidised.",
  },
  {
    name: "Ketone",
    formula: "R–CO–R'",
    nameMarker: "-one",
    example: "Propanone / acetone (CH₃COCH₃) — nail polish remover",
    whyItMatters: "Carbonyl on an internal carbon. Common solvent.",
  },
  {
    name: "Amine",
    formula: "R–NH₂",
    nameMarker: "-amine",
    example: "Methylamine (CH₃NH₂); proteins are full of them",
    whyItMatters: "Weak bases. Found in every amino acid and many drugs.",
  },
  {
    name: "Ester",
    formula: "R–COO–R'",
    nameMarker: "-oate",
    example: "Ethyl ethanoate — fruity smells, banana flavouring",
    whyItMatters: "Made from acid + alcohol. Fats are triesters of glycerol.",
  },
  {
    name: "Ether",
    formula: "R–O–R'",
    nameMarker: "-oxy- / 'ether'",
    example: "Diethyl ether (CH₃CH₂OCH₂CH₃) — historic anaesthetic",
    whyItMatters: "Largely unreactive. Common laboratory solvent.",
  },
];

/** C1–C10 carbon-chain prefixes — must be memorised. */
export const CHAIN_PREFIXES: Array<{ n: number; prefix: string; alkane: string }> =
  [
    { n: 1, prefix: "meth-", alkane: "methane" },
    { n: 2, prefix: "eth-", alkane: "ethane" },
    { n: 3, prefix: "prop-", alkane: "propane" },
    { n: 4, prefix: "but-", alkane: "butane" },
    { n: 5, prefix: "pent-", alkane: "pentane" },
    { n: 6, prefix: "hex-", alkane: "hexane" },
    { n: 7, prefix: "hept-", alkane: "heptane" },
    { n: 8, prefix: "oct-", alkane: "octane" },
    { n: 9, prefix: "non-", alkane: "nonane" },
    { n: 10, prefix: "dec-", alkane: "decane" },
  ];

export interface OrgProblem {
  id: string;
  category: OrgCategory;
  difficulty: OrgDifficulty;
  prompt: string;
  /** Optional formula / equation block. */
  formula?: string;
  options: string[];
  correct: number;
  insight: string;
}

export const ORG_PROBLEMS: OrgProblem[] = [
  // ── functional groups ───────────────────────────────────────────────────
  {
    id: "fg-1",
    category: "functional-group",
    difficulty: "easy",
    prompt: "What functional group is shown by R–OH?",
    formula: "R–OH",
    options: ["Alcohol", "Aldehyde", "Carboxylic acid", "Ether"],
    correct: 0,
    insight: "An OH group bonded to a carbon = alcohol. Suffix -ol (e.g. ethanol).",
  },
  {
    id: "fg-2",
    category: "functional-group",
    difficulty: "easy",
    prompt: "Which functional group does CH₃COOH contain?",
    formula: "CH₃COOH",
    options: ["Aldehyde", "Ketone", "Carboxylic acid", "Ester"],
    correct: 2,
    insight:
      "–COOH is the carboxylic acid group. The molecule shown is acetic acid (vinegar).",
  },
  {
    id: "fg-3",
    category: "functional-group",
    difficulty: "easy",
    prompt: "Acetone (CH₃COCH₃) belongs to which family?",
    formula: "CH₃–CO–CH₃",
    options: ["Aldehyde", "Ketone", "Ester", "Ether"],
    correct: 1,
    insight:
      "C=O on an INTERNAL carbon = ketone (-one). C=O on a TERMINAL carbon = aldehyde (-al).",
  },
  {
    id: "fg-4",
    category: "functional-group",
    difficulty: "medium",
    prompt: "Which group is responsible for the basicity of amino acids?",
    options: ["–COOH", "–OH", "–NH₂ (amine)", "–CHO"],
    correct: 2,
    insight:
      "The amine (–NH₂) lone pair on N accepts a proton — that's what makes it basic. The –COOH is acidic; together they make amino acids amphoteric.",
  },
  {
    id: "fg-5",
    category: "functional-group",
    difficulty: "medium",
    prompt:
      "Which functional group is formed when a carboxylic acid reacts with an alcohol?",
    formula: "R–COOH + R'–OH  →  ?  + H₂O",
    options: ["Ether", "Ester", "Ketone", "Amide"],
    correct: 1,
    insight:
      "Esterification: –COOH + –OH → –COO–R' + H₂O. Esters give fruity smells (banana, pear, pineapple).",
  },
  {
    id: "fg-6",
    category: "functional-group",
    difficulty: "hard",
    prompt:
      "Which group's name ends in '-al' in IUPAC nomenclature?",
    options: ["Alcohol", "Aldehyde", "Alkene", "Amine"],
    correct: 1,
    insight:
      "Aldehyde → -al. Alcohol → -ol. Alkene → -ene. Amine → -amine. Endings carry the meaning.",
  },

  // ── naming ──────────────────────────────────────────────────────────────
  {
    id: "nm-1",
    category: "naming",
    difficulty: "easy",
    prompt: "What is the IUPAC name for CH₃CH₂CH₃?",
    formula: "CH₃CH₂CH₃",
    options: ["Methane", "Ethane", "Propane", "Butane"],
    correct: 2,
    insight: "3 carbons, all single bonds → prop- + -ane = propane.",
  },
  {
    id: "nm-2",
    category: "naming",
    difficulty: "easy",
    prompt: "What is the IUPAC name for CH₃CH₂OH?",
    formula: "CH₃CH₂OH",
    options: ["Methanol", "Ethanol", "Propanol", "Ethanal"],
    correct: 1,
    insight: "2 carbons + OH → eth- + -ol = ethanol (drinking alcohol).",
  },
  {
    id: "nm-3",
    category: "naming",
    difficulty: "medium",
    prompt: "How many carbons does pentane have?",
    options: ["3", "4", "5", "6"],
    correct: 2,
    insight:
      "Memorise the prefixes: meth-1, eth-2, prop-3, but-4, pent-5, hex-6, hept-7, oct-8, non-9, dec-10.",
  },
  {
    id: "nm-4",
    category: "naming",
    difficulty: "medium",
    prompt: "What is the IUPAC name for CH₂=CHCH₃?",
    formula: "CH₂=CH–CH₃",
    options: ["Propane", "Propene", "Propyne", "Propanol"],
    correct: 1,
    insight: "3 carbons + a C=C double bond → prop- + -ene = propene.",
  },
  {
    id: "nm-5",
    category: "naming",
    difficulty: "hard",
    prompt:
      "What is the IUPAC name for CH₃–CH(OH)–CH₃?",
    formula: "CH₃–CH(OH)–CH₃",
    options: ["1-propanol", "2-propanol", "Propanal", "Propanone"],
    correct: 1,
    insight:
      "OH on the MIDDLE carbon (carbon #2) → 2-propanol (a.k.a. isopropyl alcohol / rubbing alcohol).",
  },
  {
    id: "nm-6",
    category: "naming",
    difficulty: "hard",
    prompt:
      "What is the IUPAC name for HCOOH?",
    formula: "HCOOH",
    options: ["Methanol", "Methanoic acid", "Ethanoic acid", "Methanal"],
    correct: 1,
    insight:
      "1 carbon + –COOH → meth- + -anoic acid = methanoic acid (formic acid; ant venom).",
  },

  // ── isomers ─────────────────────────────────────────────────────────────
  {
    id: "is-1",
    category: "isomers",
    difficulty: "easy",
    prompt: "What are structural isomers?",
    options: [
      "Different compounds with the same molecular formula but different connectivity.",
      "Identical compounds drawn differently.",
      "Compounds with different molecular formulas.",
      "Mirror images of each other only.",
    ],
    correct: 0,
    insight:
      "Same formula, different skeleton. e.g. butane (straight) and 2-methylpropane (branched) are both C₄H₁₀.",
  },
  {
    id: "is-2",
    category: "isomers",
    difficulty: "medium",
    prompt:
      "How many structural isomers does C₄H₁₀ have?",
    formula: "C₄H₁₀",
    options: ["1", "2", "3", "4"],
    correct: 1,
    insight:
      "Two: butane (straight chain) and 2-methylpropane / isobutane (branched). C₅H₁₂ has three.",
  },
  {
    id: "is-3",
    category: "isomers",
    difficulty: "medium",
    prompt:
      "Are 1-propanol (CH₃CH₂CH₂OH) and 2-propanol (CH₃CH(OH)CH₃) isomers?",
    options: [
      "No, they are different compounds with different formulas.",
      "Yes, both are C₃H₈O — same formula, different OH position.",
      "No, 2-propanol is a ketone.",
      "Yes, they are mirror images.",
    ],
    correct: 1,
    insight:
      "Both have formula C₃H₈O. The OH position changes properties (boiling point, reactivity).",
  },

  // ── skeletal structures ─────────────────────────────────────────────────
  {
    id: "sk-1",
    category: "skeletal",
    difficulty: "easy",
    prompt:
      "In a skeletal (line-angle) structure, each unlabeled vertex represents a:",
    options: ["Hydrogen atom", "Carbon atom", "Oxygen atom", "Lone pair"],
    correct: 1,
    insight:
      "Vertices and line-ends = carbons. Hydrogens are implied to fill each carbon's 4 bonds.",
  },
  {
    id: "sk-2",
    category: "skeletal",
    difficulty: "medium",
    prompt:
      "How many hydrogens are on the central carbon of CH₃–CH(OH)–CH₃ in skeletal form?",
    options: ["0", "1", "2", "3"],
    correct: 1,
    insight:
      "The central carbon has bonds to 2 carbons + 1 OH = 3 bonds shown. Need 4 total → 1 H is implied.",
  },
  {
    id: "sk-3",
    category: "skeletal",
    difficulty: "hard",
    prompt:
      "A zig-zag of 6 vertices with no other atoms shown represents:",
    options: ["Hexene", "Hexane", "Cyclohexane", "Benzene"],
    correct: 1,
    insight:
      "6 vertices = 6 carbons, all single bonds (no double-line marks), open chain → hexane (C₆H₁₄).",
  },

  // ── general ─────────────────────────────────────────────────────────────
  {
    id: "gn-1",
    category: "general",
    difficulty: "easy",
    prompt:
      "Why is carbon the basis of organic chemistry?",
    options: [
      "Carbon is the most abundant element on Earth.",
      "Carbon forms 4 strong covalent bonds, allowing huge variety of stable structures.",
      "Carbon is a metal that conducts electricity.",
      "Carbon only forms ionic bonds.",
    ],
    correct: 1,
    insight:
      "Tetravalent + can chain with itself + bonds with H, O, N, halogens → millions of stable molecules possible.",
  },
  {
    id: "gn-2",
    category: "general",
    difficulty: "medium",
    prompt: "Saturated hydrocarbons contain only:",
    options: [
      "C=C double bonds",
      "C≡C triple bonds",
      "C–C single bonds and C–H bonds",
      "Carbon atoms (no hydrogen)",
    ],
    correct: 2,
    insight:
      "Saturated = every C has its maximum hydrogens, which means only single bonds. Alkanes are the saturated family.",
  },
  {
    id: "gn-3",
    category: "general",
    difficulty: "medium",
    prompt:
      "What is the general molecular formula for a straight-chain alkane with n carbons?",
    options: ["CₙH₂ₙ", "CₙH₂ₙ₋₂", "CₙH₂ₙ₊₂", "CₙHₙ"],
    correct: 2,
    insight:
      "Alkanes: CₙH₂ₙ₊₂. Alkenes: CₙH₂ₙ. Alkynes: CₙH₂ₙ₋₂. Each π bond removes 2 H.",
  },
];